/**
 * Client side of authentication, profile and avatar (Milestone 1).
 *
 * Passwords never leave the device: PBKDF2 runs here (src/shared/kdf.ts) and only the derived
 * 32-byte key is sent. Every function that changes the signed-in user writes the fresh MeDTO
 * into the ['me'] query cache (AuthProvider reads it) and invalidates directory queries that
 * show the user's name or photo. Signing in wipes a previous user's local data before ['me']
 * is set (adoptSession).
 */
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { normalizeEmail } from '../../shared/email';
import type {
  AcceptTokenRequest,
  ChangePasswordRequest,
  InspectSignupRequest,
  InspectSignupResponse,
  InspectTokenRequest,
  InspectTokenResponse,
  LoginRequest,
  MeDTO,
  MeResponse,
  SignupRequest,
} from '../../shared/dto/auth';
import type { ProfileUpdateRequest } from '../../shared/dto/members';
import { CURRENT_KDF_V, deriveLoginKey } from '../../shared/kdf';
import { storedLang } from '../i18n';
import { ensureSingleUser, wipeLocalData } from '../offline/wipe';
import { flushPendingChanges, PendingChangesError } from '../sync/pending';
import { putAvatarParts } from './avatar';
import { ApiFailure, api } from './client';
import { queryClient } from './queryClient';

export const ME_QUERY_KEY = ['me'] as const;

/** Store the signed-in user and refresh directory entries that show them. */
function setMe(qc: QueryClient, me: MeDTO): MeDTO {
  qc.setQueryData(ME_QUERY_KEY, me);
  void qc.invalidateQueries({ queryKey: ['members'] });
  void qc.invalidateQueries({ queryKey: ['member', me.id] });
  void qc.invalidateQueries({ queryKey: ['admin-members'] });
  return me;
}

function currentMe(qc: QueryClient): MeDTO | null {
  return qc.getQueryData<MeDTO | null>(ME_QUERY_KEY) ?? null;
}

/* ─── Sign in / out ─────────────────────────────────────────────────────── */

/**
 * Make `me` the signed-in user of this device. When a different user was signed in here before
 * (session expired / revoked without a logout, or an invite opened on someone else's iPad), their
 * cached queries, persisted cache and offline stores are wiped FIRST — while the sign-in screen
 * is still showing and nothing renders their data — and only then is ['me'] set. AuthProvider's
 * own ensureSingleUser() call is then a no-op because the last user id already matches.
 */
async function adoptSession(me: MeDTO): Promise<MeDTO> {
  try {
    await ensureSingleUser(me.id);
  } catch (e) {
    // Only the persisted-cache delete can throw (IndexedDB unavailable, e.g. private mode). The
    // in-memory queries were already removed and nothing can have been persisted, so carry on.
    console.warn('local data wipe failed', e);
  }
  queryClient.setQueryData(ME_QUERY_KEY, me);
  return me;
}

/**
 * Carry a language picked on the sign-in / invite screen (before the server knew the user) over
 * to a member who has not finished onboarding yet; new accounts start with lang 'is', so without
 * this AuthProvider would switch the UI back to Icelandic right after signing in. Members who
 * have onboarded already chose a language on the server, so theirs is kept (e.g. on a reset link).
 */
async function withPreLoginLang(me: MeDTO): Promise<MeDTO> {
  if (!me.mustOnboard) return me;
  const chosen = storedLang();
  if (chosen === me.lang) return me;
  try {
    const patch: ProfileUpdateRequest = { lang: chosen };
    return (await api<MeResponse>('/profile', { method: 'PATCH', body: patch })).me;
  } catch {
    // Not worth failing the sign-in over; the language can be picked again in onboarding/settings.
    return me;
  }
}

/** Derive the key in the browser (≈1–2 s on older iPads), then sign in. */
export async function login(email: string, password: string): Promise<MeDTO> {
  const normalized = normalizeEmail(email);
  const dk = await deriveLoginKey(normalized, password, CURRENT_KDF_V);
  const body: LoginRequest = { email: normalized, dk };
  const { me } = await api<MeResponse>('/auth/login', { method: 'POST', body });
  return adoptSession(await withPreLoginLang(me));
}

/** 401 means the session is already gone — that's fine when signing out. */
async function postIgnoring401(path: string): Promise<void> {
  try {
    await api<unknown>(path, { method: 'POST' });
  } catch (e) {
    if (e instanceof ApiFailure && e.status === 401) return;
    throw e;
  }
}

async function clearLocalSession(): Promise<void> {
  await wipeLocalData();
  queryClient.setQueryData(ME_QUERY_KEY, null);
}

export interface LogoutOptions {
  /** Sign out even though some changes could not be sent (the user confirmed losing them). */
  force?: boolean;
}

/**
 * Logout wipes the device, so first try to send queued calendar writes and annotation ops. When
 * some are still unsent, throws PendingChangesError(count) — the UI asks "N óvistaðar breytingar –
 * skrá út samt?" and calls again with { force: true }.
 */
async function ensureNothingPending(opts: LogoutOptions): Promise<void> {
  if (opts.force) return;
  const me = currentMe(queryClient);
  if (!me) return;
  const pending = await flushPendingChanges(me.id);
  if (pending > 0) throw new PendingChangesError(pending);
}

/**
 * Sign out on this device. Throws NetworkFailure when offline (nothing is wiped then, because
 * the session would still be valid on the server), PendingChangesError when unsent changes remain.
 */
export async function logout(opts: LogoutOptions = {}): Promise<void> {
  await ensureNothingPending(opts);
  await postIgnoring401('/auth/logout');
  await clearLocalSession();
}

/** End every session of this user (all devices, including this one). Same pending-changes rule as logout(). */
export async function logoutEverywhere(opts: LogoutOptions = {}): Promise<void> {
  await ensureNothingPending(opts);
  await postIgnoring401('/auth/logout-everywhere');
  // Make sure this device is signed out too, whatever the server did with the current session.
  await postIgnoring401('/auth/logout').catch(() => undefined);
  await clearLocalSession();
}

/* ─── Invite / reset links ──────────────────────────────────────────────── */

/** Who is this link for? Throws ApiFailure(410, 'token_invalid') for expired/used links. */
export function inspectToken(token: string): Promise<InspectTokenResponse> {
  const body: InspectTokenRequest = { token };
  return api<InspectTokenResponse>('/auth/token/inspect', { method: 'POST', body });
}

/** Set the password from an invite/reset link; signs in on success. */
export async function acceptToken(token: string, email: string, password: string, kdfV: number = CURRENT_KDF_V): Promise<MeDTO> {
  const dk = await deriveLoginKey(normalizeEmail(email), password, kdfV);
  const body: AcceptTokenRequest = { token, dk };
  const { me } = await api<MeResponse>('/auth/token/accept', { method: 'POST', body });
  return adoptSession(await withPreLoginLang(me));
}

/* ─── Invite links by voice ─────────────────────────────────────────────── */

/** Which section (and title) is this link for? Throws ApiFailure(410, 'token_invalid') for expired/used/revoked links. */
export function inspectSignup(token: string): Promise<InspectSignupResponse> {
  const body: InspectSignupRequest = { token };
  return api<InspectSignupResponse>('/auth/signup/inspect', { method: 'POST', body });
}

export interface SignupDetails {
  fullName: string;
  email: string;
  phone: string | null;
  password: string;
}

/**
 * Create the account from an invite link by voice and sign in. 409 conflict 'email_taken' when the e-mail
 * already has an account (the link stays valid).
 */
export async function signup(token: string, details: SignupDetails, kdfV: number = CURRENT_KDF_V): Promise<MeDTO> {
  const email = normalizeEmail(details.email);
  const dk = await deriveLoginKey(email, details.password, kdfV);
  const body: SignupRequest = { token, email, fullName: details.fullName, phone: details.phone, dk };
  const { me } = await api<MeResponse>('/auth/signup', { method: 'POST', body });
  return adoptSession(await withPreLoginLang(me));
}

/**
 * Change password (both keys derived here; other sessions are signed out by the server).
 * `currentKdfV` should be me.kdfV.
 */
export async function changePassword(
  email: string,
  current: string,
  next: string,
  currentKdfV: number = CURRENT_KDF_V,
): Promise<void> {
  const normalized = normalizeEmail(email);
  const [currentDk, newDk] = await Promise.all([
    deriveLoginKey(normalized, current, currentKdfV),
    deriveLoginKey(normalized, next, CURRENT_KDF_V),
  ]);
  const body: ChangePasswordRequest = { currentDk, newDk };
  const res = await api<Partial<MeResponse> | undefined>('/auth/password', { method: 'POST', body });
  if (res?.me) queryClient.setQueryData(ME_QUERY_KEY, res.me);
}

/* ─── Profile ───────────────────────────────────────────────────────────── */

export async function updateProfile(patch: ProfileUpdateRequest, qc: QueryClient = queryClient): Promise<MeDTO> {
  const { me } = await api<MeResponse>('/profile', { method: 'PATCH', body: patch });
  return setMe(qc, me);
}

/** PATCH /api/profile as a mutation; updates ['me'] on success. */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['profile'],
    mutationFn: (patch: ProfileUpdateRequest) => updateProfile(patch, qc),
    networkMode: 'always',
  });
}

/* ─── Avatar ────────────────────────────────────────────────────────────── */

/**
 * Upload a new avatar (512 px then 128 px JPEG) under the next revision, then commit it.
 * The first guess is avatarRev + 1; a 409 stale_avatar_rev (after a removal, or a change on another
 * device or by an editor/admin) is retried once with the revision the server expects (putAvatarParts).
 */
export async function uploadAvatar(me: MeDTO, blob512: Blob, blob128: Blob): Promise<MeDTO> {
  let base = currentMe(queryClient) ?? me;
  if (base.id !== me.id) base = me;
  const guess = Math.max(base.avatarRev, me.avatarRev) + 1;
  const rev = await putAvatarParts('/avatar', guess, blob512, blob128, async () => (await api<MeResponse>('/auth/me')).me.avatarRev + 1);
  const { me: next } = await api<MeResponse>('/avatar/commit', { method: 'POST', body: { rev } });
  return setMe(queryClient, next);
}

/** Remove the avatar (initials are shown instead). */
export async function removeAvatar(): Promise<MeDTO> {
  const { me } = await api<MeResponse>('/avatar', { method: 'DELETE' });
  return setMe(queryClient, me);
}
